import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { useNow } from '@/components/ui/Countdown';
import { AdPlaceholder } from './AdPlaceholder';
import { skipBetweenCallsAd } from './betweenCalls';
import { useAdsLiveSync } from './live';
import { useAdOverlay } from './overlay';
import { isAdExcludedPath, peekAdProvider } from './runtime';

/** Seconds left on a fake creative's timer. */
function useSecondsLeft(total: number): number {
  const [start] = useState(() => Date.now());
  const now = useNow(250);
  return Math.max(0, Math.ceil(total - (now - start) / 1000));
}

function FakeRewarded({ seconds, onDone }: { seconds: number; onDone: (r: 'granted' | 'closed') => void }) {
  const left = useSecondsLeft(seconds);
  const finished = left === 0;
  return (
    <div className="fixed inset-0 z-[95] flex flex-col items-center justify-center bg-ground px-5" role="dialog" aria-modal="true" aria-label="Ad" data-testid="ad-rewarded-overlay">
      <div className="w-full max-w-sm">
        <div className="mb-3 flex items-center justify-between text-[13px] font-medium text-muted">
          <span className="rounded-full bg-raised px-2.5 py-0.5 font-semibold text-fg">Ad</span>
          <span role="timer" aria-live="off" className="tabular-nums">
            {finished ? 'Reward unlocked' : `Reward in ${left}s`}
          </span>
        </div>
        <AdPlaceholder className="min-h-[320px]" rounded="rounded-card" note="Test mode: no real ad loaded" />
        <Button block className="mt-4" accent="paper" variant={finished ? 'solid' : 'outline'} onClick={() => onDone(finished ? 'granted' : 'closed')} data-testid="ad-rewarded-close">
          {finished ? 'Collect reward' : 'Close (no reward)'}
        </Button>
      </div>
    </div>
  );
}

function Interstitial({ fake, onContinue }: { fake: { seconds: number } | null; onContinue: () => void }) {
  const left = useSecondsLeft(fake?.seconds ?? 0);
  return (
    <div className="fixed inset-0 z-[95] flex flex-col items-center justify-center bg-ground px-5" role="dialog" aria-modal="true" aria-label="Ad" data-testid="ad-interstitial">
      <div className="w-full max-w-sm text-center">
        <span className="rounded-full bg-raised px-2.5 py-0.5 text-[13px] font-semibold text-fg">Ad</span>
        {fake ? (
          <>
            <AdPlaceholder className="mt-3 min-h-[320px]" rounded="rounded-card" note="Test mode: no real ad loaded" />
            <Button block className="mt-4" accent="paper" variant="outline" disabled={left > 0} onClick={onContinue} data-testid="ad-interstitial-continue">
              {left > 0 ? `Continue in ${left}` : 'Continue'}
            </Button>
          </>
        ) : (
          <>
            <p className="mt-4 text-[22px] font-semibold leading-tight text-fg">A quick break</p>
            <p className="mt-1 text-[15px] text-muted">Plus is ad-free.</p>
            <button type="button" onClick={skipBetweenCallsAd} className="mt-6 h-11 rounded-full px-5 text-[15px] font-semibold text-fg transition-colors hover:bg-raised focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg" data-testid="ad-interstitial-skip">
              Skip
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Global ad host: tears every slot down when the person enters an excluded
 * screen or an admin switches ads off at runtime, and draws the ad chrome (between-calls backdrop, test-mode
 * placeholder creatives). Renders nothing when no ad is on screen.
 */
export function AdsHost() {
  const { pathname } = useLocation();
  const overlay = useAdOverlay((s) => s.overlay);
  const set = useAdOverlay((s) => s.set);
  // Admin switched ads off/on or a placement (runtime flags, ARCHITECTURE §22).
  useAdsLiveSync();

  useEffect(() => {
    if (!isAdExcludedPath(pathname)) return;
    peekAdProvider()?.destroyAll();
    // A rewarded placeholder never follows the person into a call/pod/chat.
    const o = useAdOverlay.getState().overlay;
    if (o?.kind === 'fake-rewarded') o.resolve('closed');
  }, [pathname]);

  if (!overlay) return null;
  if (overlay.kind === 'fake-rewarded') return <FakeRewarded seconds={overlay.seconds} onDone={overlay.resolve} />;
  if (overlay.kind === 'fake-interstitial')
    return (
      <Interstitial
        fake={{ seconds: overlay.seconds }}
        onContinue={() => {
          set(null);
          overlay.resolve();
        }}
      />
    );
  return <Interstitial fake={null} onContinue={skipBetweenCallsAd} />;
}
